/** Header identity chip: who this tab is (actor and role) and a switcher between the identities logged in in this
 * browser. Several people and bots can share one browser; each tab keeps its own identity (see lib/identity). */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type Schemas, unwrap } from "../api/client";
import { qk } from "../api/keys";
import { getSlot, setSlot } from "../lib/identity";
import { useAnnounce } from "./announce";

type Identity = Schemas["BrowserIdentity"];

function label(i: { actor: string; role_title?: string | null }): string {
  return i.role_title ? `${i.actor} (${i.role_title})` : i.actor;
}

/** Switch this tab to another identity: every query refetches as that identity; the event stream reconnects. */
export function useSwitchIdentity() {
  const qc = useQueryClient();
  const announce = useAnnounce();
  return async (i: Identity | null) => {
    setSlot(i?.slot ?? null);
    await qc.resetQueries();
    if (i) announce(`This tab is now ${label(i)}`);
  };
}

export function IdentityList({ current, onPick }: { current: string | null; onPick: (i: Identity) => void }) {
  const sessions = useQuery({ queryKey: qk.authSessions, queryFn: () => unwrap(api.GET("/api/auth/sessions")) });
  if (sessions.isPending) return <p className="identity-muted">Loading identities…</p>;
  const rows = sessions.data ?? [];
  if (rows.length === 0) return <p className="identity-muted" data-testid="identity-none">No one is logged in in this browser.</p>;
  return (
    <ul className="identity-list" aria-label="Identities in this browser" data-testid="identity-list">
      {rows.map((i) => (
        <li key={i.slot}>
          <button type="button" data-testid={`identity-switch-${i.slot}`} aria-current={i.slot === current ? "true" : undefined}
            aria-label={`${i.slot === current ? "Current identity: " : "Switch this tab to "}${label(i)}`}
            disabled={i.slot === current} onClick={() => onPick(i)}>
            {label(i)}{i.default ? " · latest login" : ""}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function IdentityMenu({ me }: { me: Schemas["Me"] | undefined }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const nav = useNavigate();
  const announce = useAnnounce();
  const switchTo = useSwitchIdentity();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: KeyboardEvent | MouseEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", close);
    document.addEventListener("mousedown", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("mousedown", close);
    };
  }, [open]);

  if (!me) return <span className="me" data-testid="me" />;
  const current = me.slot ?? getSlot();

  const logout = async () => {
    setBusy(true);
    try {
      await unwrap(api.POST("/api/auth/logout"));
      announce(`Logged out ${label(me)} in this browser`);
      setOpen(false);
      const left = await unwrap(api.GET("/api/auth/sessions"));
      const next = left.find((i) => i.default) ?? left[0] ?? null;
      if (next) await switchTo(next);
      else {
        setSlot(null);
        qc.clear();
        nav("/login");
      }
    } catch (e) {
      announce(`Log out failed: ${(e as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="identity" ref={box}>
      <button type="button" className="identity-chip" data-testid="identity-chip" aria-haspopup="true" aria-expanded={open}
        aria-controls="identity-menu" aria-label={`This tab is ${label(me)}. Switch identity`} onClick={() => setOpen((o) => !o)}>
        <span className="me" data-testid="me">{me.actor}</span>
        {me.role_title && <span className="identity-role" data-testid="identity-role"> · {me.role_title}</span>}
      </button>
      {open && (
        <div id="identity-menu" className="identity-menu" data-testid="identity-menu" role="group" aria-label="Identities">
          <p className="identity-muted">This tab: <strong>{label(me)}</strong>{me.via === "bearer" ? " (token)" : ""}</p>
          <IdentityList current={current} onPick={(i) => { setOpen(false); void switchTo(i); }} />
          <div className="identity-actions">
            <a className="button" href="/login" target="_blank" rel="noopener" data-testid="identity-add"
              aria-label="Add another person (opens the login page in a new tab)">
              Add another person
            </a>
            {me.via === "session" && (
              <button type="button" data-testid="identity-logout" disabled={busy} onClick={() => void logout()}
                aria-label={`Log out ${label(me)} (only this identity)`}>
                Log out this identity
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
